import { CALL_SCRIPT_STEP_LIBRARY } from "@swivl-mono/types";
import type { CallScriptLibraryStep, CallScriptStep, CallScriptStepInterface, CallScriptStepSource, LegacyCallScriptStep } from "@swivl-mono/types";

/** A library step with where it came from — what the Steps tab and the pickers render. */
export interface ResolvedLibraryStep extends CallScriptLibraryStep {
  source: CallScriptStepSource;
  /** The org row, null for an untouched platform step. */
  rowId: number | null;
  /** For an org override of a platform step: the platform entry it replaces (for "Revert to default" and diffs). */
  platformDefault?: CallScriptLibraryStep;
}

export type CallScriptStepRowLike = Pick<
  CallScriptStepInterface,
  | "id"
  | "slug"
  | "group"
  | "label"
  | "sentence"
  | "liveAgentCopy"
  | "coachingCompletionCriteria"
  | "showToAgents"
  | "requirementsAllowed"
  | "defaultRequirements"
>;

/**
 * The step library an org actually has (ENG-3402): the platform catalog with the org's rows
 * applied — an org row whose slug is a platform slug overrides that step (all fields; `requiresPage`
 * stays platform-owned), any other org row is appended as an org step. Pure — the caller loads the
 * org's rows. Order: catalog order, then org-added steps by label.
 */
export function resolveStepLibrary(
  rows: CallScriptStepRowLike[],
  catalog: ReadonlyArray<CallScriptLibraryStep> = CALL_SCRIPT_STEP_LIBRARY
): ResolvedLibraryStep[] {
  const bySlug = new Map(rows.map((row) => [row.slug, row]));
  const out: ResolvedLibraryStep[] = catalog.map((step) => {
    const row = bySlug.get(step.slug);
    if (!row) return { ...step, source: "platform", rowId: null };
    return {
      ...step,
      group: (row.group as CallScriptLibraryStep["group"]) || step.group,
      label: row.label,
      sentence: row.sentence,
      liveAgentCopy: row.liveAgentCopy,
      coachingCompletionCriteria: row.coachingCompletionCriteria,
      showToAgents: row.showToAgents,
      requirementsAllowed: row.requirementsAllowed,
      defaultRequirements: [...row.defaultRequirements],
      source: "org",
      rowId: row.id,
      platformDefault: step
    };
  });
  const platformSlugs = new Set(catalog.map((s) => s.slug));
  const added = rows
    .filter((row) => !platformSlugs.has(row.slug))
    .map(
      (row): ResolvedLibraryStep => ({
        slug: row.slug,
        group: row.group as CallScriptLibraryStep["group"],
        label: row.label,
        sentence: row.sentence,
        liveAgentCopy: row.liveAgentCopy,
        coachingCompletionCriteria: row.coachingCompletionCriteria,
        showToAgents: row.showToAgents,
        requirementsAllowed: row.requirementsAllowed,
        defaultRequirements: [...row.defaultRequirements],
        source: "org",
        rowId: row.id
      })
    )
    .sort((a, b) => a.label.localeCompare(b.label));
  return [...out, ...added];
}

/**
 * Steps as stored before ENG-3402 (`{ kind: "platform", key }` / `{ kind: "custom", text }`) read
 * as today's shape. Custom steps have no slug to map to and are dropped — the data migration
 * converted the live rows; this covers old version snapshots. Already-current steps pass through.
 */
export function normalizeCallScriptSteps(raw: unknown): CallScriptStep[] {
  if (!Array.isArray(raw)) return [];
  const out: CallScriptStep[] = [];
  for (const item of raw as Array<CallScriptStep | LegacyCallScriptStep>) {
    if (!item || typeof item !== "object") continue;
    const requirements = Array.isArray((item as { requirements?: unknown }).requirements)
      ? [...((item as { requirements: CallScriptStep["requirements"] }).requirements ?? [])]
      : [];
    if ("slug" in item && typeof item.slug === "string") {
      out.push({ slug: item.slug, requirements });
    } else if ("kind" in item && item.kind === "platform" && typeof item.key === "string") {
      out.push({ slug: item.key, requirements });
    }
  }
  return out;
}
